import React, { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import Avatar from '@mui/material/Avatar'
import Card from '../../../../components/common/Card/index'
import Typography from '../../../../components/common/Typography/index'
import Button from '../../../../components/common/Button/index'
import Miniheader from '../../../../components/layout/MiniHeader'
import { useDispatch } from 'react-redux'
import useAppSelector from '../../../../state/redux/hooks/index'
import { fetchAgents } from '../../../../state/redux/actions/agentActions'
import LoadingIndicator from '../../../../components/state/LoadingIndicator'
import ErrorState from '../../../../components/state/ErrorState'
import { formatDateDashed } from '../../../../shared/utils/dateUtils'
import { isNewUser } from '../../../../shared/utils/ageUtils'
import Pagination from '../../../../components/common/Pagination/Pagination'

const AgentList = ({ onEdit = () => {}, onDelete = () => {} }) => {
  const navigate = useNavigate()
  const [openPopup, setOpenPopup] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(50)
  const [dateFilter, setDateFilter] = useState(null)
  const [isFiltering, setIsFiltering] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dispatch = useDispatch()
  const popupRef = useRef()
  const { agents, loading, error } = useAppSelector(state => state.agent)

  // Filter agents by date range
  const filterAgentsByDate = (agentList, startDate, endDate) => {
    if (!startDate || !endDate || !agentList || !Array.isArray(agentList)) {
      return agentList || []
    }

    return agentList.filter(agent => {
      const agentDate = new Date(agent.createdAt)
      const start = new Date(startDate)
      const end = new Date(endDate)

      // Set time boundaries
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)

      return agentDate >= start && agentDate <= end
    })
  }

  // Apply date filter if active, otherwise use all agents
  const filteredAgents = isFiltering && dateFilter
    ? filterAgentsByDate(agents, dateFilter.startDate, dateFilter.endDate)
    : agents

  const newAgents = filteredAgents.filter(agent => isNewUser(agent.createdAt) || agent.verificationStatus === 'pending')

  // Apply search filter
  const searchFilteredAgents = filteredAgents.filter(agent => {
    if (!searchTerm) return true

    const searchLower = searchTerm.toLowerCase()
    return (
      agent.full_name?.toLowerCase().includes(searchLower) ||
      agent.email?.toLowerCase().includes(searchLower) ||
      agent.company?.toLowerCase().includes(searchLower) ||
      agent.company_address?.toLowerCase().includes(searchLower) ||
      agent.broker_id?.toLowerCase().includes(searchLower)
    )
  })

  // Sort agents to display new users at the top
  const sortedAgents = [...searchFilteredAgents].sort((a, b) => {
    const aIsNew = isNewUser(a.createdAt) || a.verificationStatus === 'pending'
    const bIsNew = isNewUser(b.createdAt) || b.verificationStatus === 'pending'

    // New users first
    if (aIsNew && !bIsNew) return -1
    if (!aIsNew && bIsNew) return 1

    // If both are new or both are not new, sort by creation date (newest first)
    return new Date(b.createdAt) - new Date(a.createdAt)
  })

  // Paginate the sorted agents
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedAgents = sortedAgents.slice(startIndex, endIndex)

  useEffect(() => {
    dispatch(fetchAgents())
  }, [dispatch])

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const handleRefresh = () => {
    setCurrentPage(1)
    dispatch(fetchAgents())
  }

  // Handle filter change from MiniHeader
  const handleMiniHeaderFilterChange = (filtered, dateRange) => {
    if (!dateRange) {
      // No date filter applied - reset to show all data
      setIsFiltering(false)
      setDateFilter(null)
      return
    }

    // Date filter is applied - set filtering state
    setIsFiltering(true)
    setDateFilter(dateRange)
    setCurrentPage(1) // Reset to first page when filtering
  }

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = e => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setOpenPopup(false)
      }
    }

    const handleEscape = e => {
      if (e.key === 'Escape') {
        setOpenPopup(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  // Helper function to check if agent is new
  const isAgentNew = (agent) => {
    return isNewUser(agent.createdAt) || agent.verificationStatus === 'pending'
  }

  // Handle agent row click to navigate to profile
  const handleAgentClick = (agentId) => {
    navigate(`/users/agents/${agentId}`)
  }

  return (
    <>
      <Miniheader
        title="User Management"
        data={agents}
        onFilterChange={handleMiniHeaderFilterChange}
      />

      <div className="ml-[250px] flex-1 px-4 py-8 max-w-full">
        {loading && <LoadingIndicator />}
        {error && <ErrorState error={error} />}
        {!loading && agents.length === 0 && (
          <Card variant="" className="text-center p-8 border border-border rounded-md max-w-md w-full mt-[140px]">
            <Typography variant="body" className="text-secondary">
              No agents found. Add new agents to manage accounts.
            </Typography>
          </Card>
        )}
        {!loading && !error && agents.length > 0 && (
          <>
            <div className="flex justify-between py-4 gap-4">
              <div className="flex flex-start gap-4 items-center">
                {/* total agent */}
                <Card className="mt-[4px] w-[200px]">
                <div className="flex flex-col p-4">
                  <Typography> Total Agents</Typography>
                  <Typography>{agents.length}</Typography>
                </div>
              </Card>

                {/* active agent */}
                <Card className="mt-[4px] w-[200px]">
                <div className="flex flex-col p-4">
                  <Typography>Active</Typography>
                  <div className="flex justify-between ">
                    <Typography>{`${agents.filter(agent => agent.status === 'active').length}`}</Typography>
                    <div className="flex">
                      <Typography variant="body">
                        {Math.round((agents.filter(agent => agent.status === 'active').length / agents.length) * 100)}
                        %
                      </Typography>
                      <i className="fa-solid fa-arrow-up text-green-500" />
                    </div>
                  </div>
                </div>
              </Card>

              </div>
              {/* navigation card*/}
              <div className="">
                <div className="flex flex-col p-4">
                  <div
                    className="flex justify-end items-center relative cursor-pointer"
                    onClick={() => setOpenPopup(prev => !prev)}
                  >
                    <div className="relative">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="fa-solid fa-user-group text-blue-600 text-lg" />
                      </div>
                      {newAgents.length > 0 && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-medium">{newAgents.length}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {openPopup && (
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex justify-end">
                      <div ref={popupRef} className="relative flex items-center mr-4">
                        <Card className="p-2" tabIndex={0} autoFocus>
                          <Typography variant="h4" align="center" className="py-2">
                            New Users
                          </Typography>
                          <div>
                            {newAgents.map(agent => (
                              <div key={agent._id} className="flex items-center gap-2 w-full p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                                <Avatar src={'/assets/images/house.jpg'} alt="profile" className="h-8 w-8" />
                                <div className="flex-1">
                                  <Typography variant="body" className="font-medium">{agent.full_name}</Typography>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="primary"
                                    size="small"
                                    onClick={() => onEdit(agent._id)}
                                  >
                                    Validate
                                  </Button>
                                  <Button variant="danger" size="small" onClick={() => onDelete(agent._id)}>
                                    Disapprove
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </Card>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end px-2 text-primary">
                    <Typography className="caption">New Users</Typography>
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    {/* Search Bar */}
                    <div className="relative w-[300px]">
                      
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder=""
                        className="block w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-full leading-5 bg-white placeholder-gray-400 focus:outline-none focus:placeholder-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <i className="fa-solid fa-search text-gray-400 text-lg" />
                      </div>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm('')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          <i className="fa-solid fa-times text-gray-400 hover:text-gray-600 text-xs" />
                        </button>
                      )}
                    </div>
                    <Pagination
                      currentPage={currentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={sortedAgents.length}
                      onPageChange={setCurrentPage}
                      onRefresh={handleRefresh}
                      justify="end"
                    />
                  </div>
                  {searchTerm && (
                    <div className="flex justify-end px-2 mt-1">
                      <div className="text-xs text-gray-600">
                        {searchFilteredAgents.length} result{searchFilteredAgents.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* agent list */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
              <table className="min-w-full table-auto">
                <thead className="bg-white ">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-black  tracking-wider w-48">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-black  tracking-wider w-56">
                      Email address
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-black  tracking-wider w-40">
                      Company name
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-black  tracking-wider w-48">
                      Company address
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-black  tracking-wider w-32">
                      Broker ID
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-black tracking-wider w-32">
                      Sign up date
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-black  tracking-wider w-36">
                      Download store
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedAgents.map((agent, index) => (
                    <tr 
                      key={agent._id} 
                      className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                      onClick={() => handleAgentClick(agent._id)}
                    >
                      <td className="px-4 py-4 w-48">
                        <div className="flex items-center gap-3">
                          <div className="text-sm font-medium text-gray-500 w-6">
                            {startIndex + index + 1}
                          </div>
                          <div className="flex-shrink-0 h-8 w-8">
                            <Avatar
                              alt="Profile Picture"
                              src={'/assets/images/house.jpg'}
                              className="h-8 w-8"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-xs font-medium text-gray-900">
                              {agent.full_name}
                            </div>
                            {isAgentNew(agent) && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full">
                                New User
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 w-56">
                        <div className="text-xs text-gray-400 truncate">
                          {agent.email}
                        </div>
                      </td>
                      <td className="px-4 py-4 w-40">
                        <div className="text-xs text-gray-900 truncate">
                          {agent.company}
                        </div>
                      </td>
                      <td className="px-4 py-4 w-48">
                        <div className="text-xs text-gray-900 truncate">
                          {agent.company_address}
                        </div>
                      </td>
                      <td className="px-4 py-4 w-32">
                        <div className="text-xs text-gray-900">
                          {agent.broker_id}
                        </div>
                      </td>
                      <td className="px-4 py-4 w-32">
                        <div className="text-xs text-gray-400">
                          {formatDateDashed(agent.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-4 w-36">
                        <div className="text-xs text-gray-900">
                          {agent?.device_type || 'App Store'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-500">
                <span>1-50 of 100</span>
              </div>
              <div className="flex items-center space-x-2">
                <button className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50" disabled>
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded">
                  1
                </button>
                <button className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700">
                  2
                </button>
                <button className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700">
                  3
                </button>
                <span className="px-2 text-gray-500">...</span>
                <button className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700">
                  8
                </button>
                <button className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700">
                  9
                </button>
                <button className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700">
                  10
                </button>
                <button className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700">
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}

AgentList.propTypes = {
  agents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      agency: PropTypes.string,
      contact: PropTypes.string,
      status: PropTypes.string,
    })
  ),
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
}

export default AgentList